import styles from './AddStudyPlan.module.scss';
import Modal from 'src/components/modal/Modal';
import Button from 'src/components/button/Button';
import { TextInput, TextInputValue } from 'src/components/text-input/TextInput';
import { useCallback, useEffect, useState, useMemo } from 'react';
import Spinner from '@src/components/spinner/Spinner';
import Select from '@components/select/Select';
import { SelectOption } from '@src/types';
import { StudyPlanContentFragment } from '@src/api/graphql-codegen';
import { parentPlanName } from '@src/constants';

type Props = {
  open: boolean;
  creating: boolean;
  studyPlans: StudyPlanContentFragment[];
  onClose: () => void;
  onCreateNew: (name: string) => void;
  onAddUnitToStudyPlan?: (id: string) => void;
  studyPlansForSelectedUnitIds: string[];
};

function AddStudyPlan(props: Props) {
  const { open, creating, studyPlans, onClose, onCreateNew, onAddUnitToStudyPlan, studyPlansForSelectedUnitIds } = props;
  const [ name, setName ] = useState("")
  const [ error, setError ] = useState("")
  const [ selectedStudyPlan, setStudyPlan ] = useState<string>();

  const handleClose = () => {
    onClose();
  };

  useEffect(() => {
    const onPageLoad = () => {
      const item = document.getElementById("name")
      if (item) {
        item.focus();
      }
    };
    if (document.readyState === 'complete') {
      onPageLoad();
    } else {
      window.addEventListener('load', onPageLoad, false);
      return () => window.removeEventListener('load', onPageLoad);
    }

  }, [])

  const handleChange = (value: TextInputValue) => {
    setName(value.toString())
    setError('')
  }
  const handleCreateNew = useCallback(() => {
    if (name) {
      const exist = studyPlans.find(plan => plan.name.toLocaleLowerCase() === name.toLocaleLowerCase())
      if (exist) {
        setError(`${name} is exist already, Study Plan name should be unique.`)
        return;
      }
      onCreateNew(name)
    } else if (selectedStudyPlan && onAddUnitToStudyPlan) {
      onAddUnitToStudyPlan?.(selectedStudyPlan);
    }
  }, [name, selectedStudyPlan, studyPlans, onCreateNew, onAddUnitToStudyPlan])

  const onChangeStudyPlan = useCallback((o: SelectOption) => {
    setStudyPlan(o.value);
  }, []);

  const existingStudyPlanOptions = useMemo(() => (studyPlans || [])
    .filter(plan => !studyPlansForSelectedUnitIds.includes(plan.id) && !plan.name.includes(parentPlanName))
    .map(plan => ({
      value: plan.id,
      label: plan.name,
    })), [studyPlans, studyPlansForSelectedUnitIds]);

  return (
    <Modal open={open} onClose={handleClose} className={styles.wrapper}>
      <div>
        <div className={styles.title}>Name your new Study Plan:</div>
        <div className={styles.subTitle}>{`This would typically be a child’s first name`}</div>
        <div className={styles.content}>
          <TextInput hasError={error!==''} subTitle={error} id="name" placeholder={'Study plan name'} value={name} label={''} onChange={handleChange} />

          {studyPlans?.length > 0 && onAddUnitToStudyPlan ? (
            <div className={styles.studyPlanSelect}>
              <Select
                inDropDown={true}
                value={selectedStudyPlan}
                onChange={onChangeStudyPlan}
                label={'Or add to existing Study Plan:'}
                options={existingStudyPlanOptions}
                showSearch={false}
                disabled={creating || !!name}
                menuClassName={styles.selectorStyle}
              />
            </div>
          ) : null}

          <div className={styles.action}>
            <Button disabled={creating || (!selectedStudyPlan && !name)} leftIcon={creating ? <Spinner width={20} height={20} /> : undefined} className={styles.okButton} onClick={handleCreateNew} label={'SAVE'}/>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default AddStudyPlan;
